// TopPerformers — Inicio's "Top del mes" tabbed leaderboard. Four lists:
//
//   🏆 Top Performer — highest propinas/ventas ratio, gated to staff with
//      eventCount ≥ team mean. The prestige tab — rewards efficiency rather
//      than raw volume. Viewers below the threshold see "Necesitas N eventos
//      para entrar al ranking" so the gate is explicit.
//
//   🔥 Most Active — most events worked. Volume tab; rewards consistency.
//
//   💰 Ventas — total RD$ ventas this period.
//   🪙 Propinas — total RD$ propinas this period.
//
// Viewer position pins always at the bottom regardless of in-top-5 state.
import { useEffect, useState } from 'preact/hooks';
import {
  getTopPerformers,
  type TopPerformersResponse,
  type LeaderboardEntry,
  type ViewerPosition,
  type PerformerViewerPosition,
} from '../lib/api';
import { Skeleton } from './Skeleton';

type Tab = 'performer' | 'mostActive' | 'ventas' | 'propinas';

const TAB_META: Record<Tab, { label: string; emoji: string }> = {
  performer: { label: 'Top', emoji: '🏆' },
  mostActive: { label: 'Activos', emoji: '🔥' },
  ventas: { label: 'Ventas', emoji: '💰' },
  propinas: { label: 'Propinas', emoji: '🪙' },
};

export function TopPerformers() {
  const [data, setData] = useState<TopPerformersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('performer');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await getTopPerformers();
      setData(r);
    } catch (e) {
      setError((e as Error).message || 'No pudimos cargar el ranking');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div class="top-performers card">
        <div class="top-performers-header">
          <Skeleton width="40%" height={16} />
        </div>
        <div style={{ padding: '4px 14px 14px' }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={18} style={{ marginBottom: 8 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div class="top-performers card" style={{ padding: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--tg-hint-color, #999)' }}>
          No pudimos cargar el top de este mes.
        </div>
        <button class="btn ghost small" onClick={load} style={{ marginTop: 8 }}>
          Reintentar
        </button>
      </div>
    );
  }

  const entries = data[tabToKey(tab)];
  const headerTitle = tab === 'performer'
    ? `🏆 Top Performer · ${data.period.label}`
    : `🏆 Top de ${data.period.label}`;

  return (
    <div class={`top-performers card ${tab === 'performer' ? 'performer-tab' : ''}`}>
      <div class="top-performers-header">
        <span class="top-performers-title">{headerTitle}</span>
      </div>

      <div class="top-performers-tabs" role="tablist">
        {(['performer', 'mostActive', 'ventas', 'propinas'] as const).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            class={`top-performers-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {TAB_META[t].emoji} {TAB_META[t].label}
          </button>
        ))}
      </div>

      {tab === 'performer' && (
        <div class="top-performers-caption">
          Eficiencia: propinas/ventas. Mín. <strong>{data.qualifyThreshold}</strong>{' '}
          {data.qualifyThreshold === 1 ? 'evento' : 'eventos'} y{' '}
          <strong>{formatDOP(data.ventasThreshold)}</strong> en ventas para entrar.
        </div>
      )}

      {tab === 'performer' && entries.length === 0 ? (
        <div class="top-performers-empty">
          Aún no hay nadie que cumpla los mínimos este mes. Trabaja más eventos
          y suma ventas para entrar al ranking.
        </div>
      ) : entries.length === 0 ? (
        <div class="top-performers-empty">Aún no hay datos suficientes este mes.</div>
      ) : (
        <ol class="top-performers-list">
          {entries.map((e) => (
            <LeaderboardRow key={e.personalId} entry={e} tab={tab} />
          ))}
        </ol>
      )}

      {renderViewerFooter(data, tab)}
    </div>
  );
}

function renderViewerFooter(data: TopPerformersResponse, tab: Tab) {
  if (tab === 'performer') {
    const v = data.viewer.performer;
    if (!v) return null;
    if (v.qualifies) {
      return (
        <div class="top-performers-viewer">
          Tu posición: <strong>#{v.rank}</strong> de {v.total}
          {' · '}
          {formatRatio(v.tipsRatio ?? null)}
        </div>
      );
    }
    // Failed gate — tell the viewer which one and the exact gap
    if (v.failedGate === 'no_events') {
      return (
        <div class="top-performers-viewer">
          Aún no has trabajado eventos este mes.
        </div>
      );
    }
    if (v.failedGate === 'events') {
      const gap = v.eventThreshold - v.eventCount;
      return (
        <div class="top-performers-viewer">
          Te faltan <strong>{gap}</strong> {gap === 1 ? 'evento' : 'eventos'} para entrar
          al ranking (vas en {v.eventCount} de {v.eventThreshold}).
        </div>
      );
    }
    // failedGate === 'ventas'
    const ventasGap = Math.max(0, v.ventasThreshold - (v.totalVentas ?? 0));
    return (
      <div class="top-performers-viewer">
        Te faltan <strong>{formatDOP(ventasGap)}</strong> en ventas para entrar al ranking
        (vas en {formatDOP(v.totalVentas ?? 0)} de {formatDOP(v.ventasThreshold)}).
      </div>
    );
  }

  const v = data.viewer[tab];
  if (!v) return null;
  if (v.rank == null) {
    return (
      <div class="top-performers-viewer">
        Aún no has trabajado eventos este mes.
      </div>
    );
  }
  return (
    <div class="top-performers-viewer">
      Tu posición: <strong>#{v.rank}</strong> de {v.total}
      {' · '}
      {viewerValue(v, tab)}
    </div>
  );
}

function LeaderboardRow({ entry, tab }: { entry: LeaderboardEntry; tab: Tab }) {
  return (
    <li class={`top-performers-row ${entry.isViewer ? 'viewer' : ''}`}>
      <span class="top-performers-rank">{rankMedal(entry.rank)}</span>
      <span class="top-performers-name">
        {entry.displayName}
        {entry.isViewer && <span class="you-badge"> (tú)</span>}
      </span>
      <span class="top-performers-value">{rowValue(entry, tab)}</span>
    </li>
  );
}

function rankMedal(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `${rank}.`;
}

function rowValue(entry: LeaderboardEntry, tab: Tab): string {
  if (tab === 'performer') return formatRatio(entry.tipsRatio ?? null);
  if (tab === 'mostActive') return formatEventCount(entry.eventCount);
  if (tab === 'ventas') return formatDOP(entry.totalVentas ?? 0);
  return formatDOP(entry.totalPropinas ?? 0); // propinas
}

function viewerValue(viewer: ViewerPosition, tab: Tab): string {
  if (tab === 'mostActive') return formatEventCount(viewer.eventCount);
  if (tab === 'ventas') return formatDOP(viewer.totalVentas ?? 0);
  if (tab === 'propinas') return formatDOP(viewer.totalPropinas ?? 0);
  // tab === 'performer' is handled in renderViewerFooter, but TS needs an exhaustive return
  const v = viewer as PerformerViewerPosition;
  return formatRatio(v.tipsRatio ?? null);
}

function formatEventCount(n: number): string {
  return `${n} ${n === 1 ? 'evento' : 'eventos'}`;
}

function formatDOP(n: number): string {
  return `RD$ ${Math.round(n).toLocaleString('es-DO')}`;
}

function formatRatio(ratio: number | null): string {
  if (ratio == null) return '—';
  // Propinas / ventas → percentage with one decimal. Typical DR range 8–25%.
  return `${(ratio * 100).toFixed(1)}%`;
}

function tabToKey(t: Tab): 'topPerformer' | 'mostActive' | 'topVentas' | 'topPropinas' {
  if (t === 'performer') return 'topPerformer';
  if (t === 'mostActive') return 'mostActive';
  if (t === 'ventas') return 'topVentas';
  return 'topPropinas';
}
